import React, { ChangeEvent, FC, LegacyRef, useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Col,
  Row,
} from 'reactstrap';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { uploadFile, removeFile } from 'services/fileService';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { addNewSubProperty } from 'services/subPropertyService';
import { updateSubProperty } from 'services/subPropertyService';
import FloorSelect from 'Components/Common/FloorSelect';
import { FilePreview } from 'Components/Common/FilePreview';
import { uploadFieldCategoryMap } from 'Components/constants/uploadFieldCategoryMap';

type UploadedFile = {
  url: string;
  uploadedAt: number;
};

type UploadField =
  | 'jantriDocument'
  | 'houseTaxBill'
  | 'caseDocument'
  | 'photos'
  | 'commercialPropertiesList'
  | 'residentialPropertiesList'
  | 'agriculturalLandsOnRent'
  | 'tenantDisputeList'
  | 'litigationCopies'
  | 'rentReceipts'
  | 'waterBills'
  | 'propertyTaxBills'
  | 'gasConnectionBills'
  | 'digitalRecordsOfBills'
  | 'listOfCases'
  | 'caseFiles'
  | 'interimFinalOrders'
  | 'judgmentCopies'
  | 'appealsFiled'
  | 'closedCases'
  | 'ongoingCases'
  | 'advocateDetails';

interface SubPropertyModalProps {
  props: {
    floor: number;
    cellar?: boolean;
  };
  isOpen: boolean;
  toggle: () => void;
  parentPropertyId: number;
  organizationName: string;

  trustRegNumber: string;

  surveyNumber: string;

  propertyName: string;
  onSuccess: () => void;
  editData?: any;
}

type FormValues = {
  name: string;
  shopNumber: string;
  floor: string;
  size?: number;
  rentIncrementPercentage?: number;
  rentIncrementDuration?: number;
  rentAmount?: number;
  disputeReminder?: string;
  taxReminder?: string;
  assesseeNumber?: string;
  stampDuty?: number;
  lastRenovation?: string;
  jantriDate?: string;
  customFloor?: string;
  caseNumber?: string;
  caseReminder?: string;
  disputeReminderMargin?: number;
  taxReminderMargin?: number;
  jantri?: number;
  purchaseConsideration?: number;
  jantriDocument?: string;
  houseTaxBill?: string;
  photos?: string;
  commercialPropertiesList?: string;
  residentialPropertiesList?: string;
  agriculturalLandsOnRent?: string;
  tenantDisputeList?: string;
  litigationCopies?: string;
  waterBills?: string;
  propertyTaxBills?: string;
  listOfCases?: string;
  caseFiles?: string;
  interimFinalOrders?: string;
  judgmentCopies?: string;
  appealsFiled?: string;
  closedCases?: string;
  ongoingCases?: string;
};

const numTransform = (_value: unknown, originalValue: unknown) => {
  if (
    originalValue === '' ||
    originalValue === null ||
    originalValue === undefined
  ) {
    return undefined;
  }
  const n =
    typeof originalValue === 'string'
      ? parseFloat(originalValue)
      : (originalValue as number);
  return Number.isNaN(n) ? undefined : n;
};

type RHFInputField = {
  onChange: (v: unknown) => void;
  onBlur: () => void;
  value: unknown;
  name: string;
  // reactstrap Input uses a component ref, not HTMLInputElement
  ref: LegacyRef<any>;
};

const optionalNumberInputProps = (field: RHFInputField) => ({
  ...field,
  value: (field.value ?? '') as string | number,
  onChange: (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    field.onChange(v === '' ? undefined : Number(v));
  },
});

const optionalDateInputProps = (field: RHFInputField) => ({
  ...field,
  value: (field.value ?? '') as string | number,
  onChange: (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    field.onChange(v === '' ? undefined : v);
  },
});

const optionalTextInputProps = (field: RHFInputField) => ({
  ...field,
  value: (field.value ?? '') as string | number,
  onChange: (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    field.onChange(v === '' ? undefined : v);
  },
});

const nullIfUndefinedNumber = (v: number | undefined | null): number | null =>
  v == null ? null : v;

const OPTIONAL_EDIT_NULL_TO_UNDEFINED = [
  'purchaseConsideration',
  'rentAmount',
  'size',
  'jantri',
  'stampDuty',
  'rentIncrementPercentage',
  'rentIncrementDuration',
  'disputeReminderMargin',
  'taxReminderMargin',
  'assesseeNumber',
  'caseNumber',
  'customFloor',
  'disputeReminder',
  'taxReminder',
  'caseReminder',
  'jantriDate',
  'lastRenovation',
] as const;

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Please enter the property name!'),
  size: Yup.number()
    .transform(numTransform)
    .integer('Size must be a whole number')
    .positive('Size must be greater than 0')
    .nullable()
    .optional(),
  shopNumber: Yup.string().required('Shop/house number is required!'),
  floor: Yup.string().required('Floor number is required!'),
  customFloor: Yup.string().nullable().optional(),
  rentIncrementPercentage: Yup.number()
    .transform(numTransform)
    .integer('Rent increment percentage must be a whole number')
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  rentIncrementDuration: Yup.number()
    .transform(numTransform)
    .integer('Rent increment duration must be a whole number')
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  rentAmount: Yup.number()
    .transform(numTransform)
    .integer('Rent amount must be a whole number')
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  disputeReminder: Yup.string().nullable().optional(),
  taxReminderMargin: Yup.number()
    .transform(numTransform)
    .integer('Tax reminder margin must be a whole number')
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  taxReminder: Yup.string().nullable().optional(),
  caseReminder: Yup.string().nullable().optional(),
  disputeReminderMargin: Yup.number()
    .transform(numTransform)
    .integer('Dispute reminder margin must be a whole number')
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  assesseeNumber: Yup.string().nullable().optional(),
  stampDuty: Yup.number()
    .transform(numTransform)
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  caseNumber: Yup.string().nullable().optional(),
  jantriDate: Yup.string().nullable().optional(),
  lastRenovation: Yup.string().nullable().optional(),
  jantri: Yup.number()
    .transform(numTransform)
    .integer('Jantri must be a whole number')
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  purchaseConsideration: Yup.number()
    .transform(numTransform)
    .integer('Purchase consideration must be a whole number')
    .min(0, 'Must be 0 or positive')
    .nullable()
    .optional(),
  jantriDocument: Yup.string().nullable().optional(),
  houseTaxBill: Yup.string().nullable().optional(),
  photos: Yup.string().nullable().optional(),
  commercialPropertiesList: Yup.string().nullable().optional(),
  residentialPropertiesList: Yup.string().nullable().optional(),
  agriculturalLandsOnRent: Yup.string().nullable().optional(),
  tenantDisputeList: Yup.string().nullable().optional(),
  litigationCopies: Yup.string().nullable().optional(),
  waterBills: Yup.string().nullable().optional(),
  propertyTaxBills: Yup.string().nullable().optional(),
  listOfCases: Yup.string().nullable().optional(),
  caseFiles: Yup.string().nullable().optional(),
  interimFinalOrders: Yup.string().nullable().optional(),
  judgmentCopies: Yup.string().nullable().optional(),
  appealsFiled: Yup.string().nullable().optional(),
  closedCases: Yup.string().nullable().optional(),
  ongoingCases: Yup.string().nullable().optional(),
});

const SubPropertyModal: FC<SubPropertyModalProps> = ({
  props,
  isOpen,
  toggle,
  parentPropertyId,
  organizationName,
  trustRegNumber,
  surveyNumber,
  propertyName,
  onSuccess,
  editData,
}) => {
  const [isCustomFloor, setIsCustomFloor] = useState(false);
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      name: '',
      shopNumber: '',
      floor: '',
      size: undefined,
      rentIncrementPercentage: undefined,
      rentIncrementDuration: undefined,
      rentAmount: undefined,
      disputeReminder: undefined,
      taxReminder: undefined,
      caseReminder: undefined,
      disputeReminderMargin: undefined,
      taxReminderMargin: undefined,
      assesseeNumber: undefined,
      jantriDate: undefined,
      stampDuty: undefined,
      lastRenovation: undefined,
      customFloor: undefined,
      caseNumber: undefined,
      jantri: undefined,
      purchaseConsideration: undefined,
    },
  });

  const [uploads, setUploads] = useState<Record<UploadField, UploadedFile[]>>({
    jantriDocument: [],
    houseTaxBill: [],
    caseDocument: [],
    photos: [],
    commercialPropertiesList: [],
    residentialPropertiesList: [],
    agriculturalLandsOnRent: [],
    tenantDisputeList: [],
    litigationCopies: [],
    rentReceipts: [],
    waterBills: [],
    propertyTaxBills: [],
    gasConnectionBills: [],
    digitalRecordsOfBills: [],
    listOfCases: [],
    caseFiles: [],
    interimFinalOrders: [],
    judgmentCopies: [],
    appealsFiled: [],
    closedCases: [],
    ongoingCases: [],
    advocateDetails: [],
  });

  const handleMultiUpload = async (
    files: FileList | null,
    field: UploadField,
  ) => {
    if (!files || !files.length) return;

    dispatch(isVisible(true));
    try {
      const fileArr = Array.from(files);

      const results = await Promise.all(
        fileArr.map(file =>
          uploadFile({
            file,

            category: uploadFieldCategoryMap[field],

            customFileName: `${field}-${Date.now()}`,

            organizationName,

            trustRegNumber,

            surveyNumber,

            propertyName,

            subPropertyName: editData?.name,
          }),
        ),
      );
      const newFiles: UploadedFile[] = results.map(r => ({
        url: r.data.url,
        uploadedAt: Date.now(),
      }));

      setUploads(prev => {
        const updated = [...newFiles, ...prev[field]] // ⬅ DESC ORDER
          .sort((a, b) => b.uploadedAt - a.uploadedAt);

        setValue(field as any, updated.map(f => f.url).join(','));
        return { ...prev, [field]: updated };
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const removeUploadedFile = async (field: UploadField, index: number) => {
    const file = uploads[field][index];

    setUploads(prev => {
      const updated = prev[field].filter((_, i) => i !== index);
      setValue(field as any, updated.map(f => f.url).join(','));
      return { ...prev, [field]: updated };
    });

    if (file?.url) {
      try {
        await removeFile([file.url]);
      } catch (e) {
        console.error('Delete failed', e);
      }
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async values => {
    dispatch(isVisible(true));
    try {
      const payload: any = {
        ...values,
        purchaseConsideration: nullIfUndefinedNumber(
          values.purchaseConsideration,
        ),
        rentAmount: nullIfUndefinedNumber(values.rentAmount),
        size: nullIfUndefinedNumber(values.size),
        jantri: nullIfUndefinedNumber(values.jantri),
        stampDuty: nullIfUndefinedNumber(values.stampDuty),
        rentIncrementPercentage: nullIfUndefinedNumber(
          values.rentIncrementPercentage,
        ),
        rentIncrementDuration: nullIfUndefinedNumber(
          values.rentIncrementDuration,
        ),
        disputeReminderMargin: nullIfUndefinedNumber(
          values.disputeReminderMargin,
        ),
        taxReminderMargin: nullIfUndefinedNumber(values.taxReminderMargin),
        propertyId: parentPropertyId,
        photos: uploads.photos.map(f => f.url).join(','),
        jantriDocument: uploads.jantriDocument.map(f => f.url).join(','),
        houseTaxBill: uploads.houseTaxBill.map(f => f.url).join(','),
        caseDocument: uploads.caseDocument.map(f => f.url).join(','),
        commercialPropertiesList: uploads.commercialPropertiesList
          .map(f => f.url)
          .join(','),
        residentialPropertiesList: uploads.residentialPropertiesList
          .map(f => f.url)
          .join(','),
        agriculturalLandsOnRent: uploads.agriculturalLandsOnRent
          .map(f => f.url)
          .join(','),
        tenantDisputeList: uploads.tenantDisputeList.map(f => f.url).join(','),
        litigationCopies: uploads.litigationCopies.map(f => f.url).join(','),
        rentReceipts: uploads.rentReceipts.map(f => f.url).join(','),
        waterBills: uploads.waterBills.map(f => f.url).join(','),
        propertyTaxBills: uploads.propertyTaxBills.map(f => f.url).join(','),
        gasConnectionBills: uploads.gasConnectionBills
          .map(f => f.url)
          .join(','),
        digitalRecordsOfBills: uploads.digitalRecordsOfBills
          .map(f => f.url)
          .join(','),
        listOfCases: uploads.listOfCases.map(f => f.url).join(','),
        caseFiles: uploads.caseFiles.map(f => f.url).join(','),
        interimFinalOrders: uploads.interimFinalOrders
          .map(f => f.url)
          .join(','),
        judgmentCopies: uploads.judgmentCopies.map(f => f.url).join(','),
        appealsFiled: uploads.appealsFiled.map(f => f.url).join(','),
        closedCases: uploads.closedCases.map(f => f.url).join(','),
        ongoingCases: uploads.ongoingCases.map(f => f.url).join(','),
        advocateDetails: uploads.advocateDetails.map(f => f.url).join(','),
      };
      delete payload.id;
      delete payload.updatedAt;
      delete payload.createdAt;
      delete payload.tenants;
      delete payload.subPropertyId;

      const result = editData
        ? await updateSubProperty(editData.subPropertyId, payload)
        : await addNewSubProperty(payload);

      if (result?.data) {
        toast.success(
          `Sub Property ${editData ? 'updated' : 'added'} successfully`,
          { autoClose: 2000 },
        );
        onSuccess();
        reset();
        toggle();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (isOpen && editData) {
      // set form fields
      const fieldValues: any = { ...editData };
      OPTIONAL_EDIT_NULL_TO_UNDEFINED.forEach(key => {
        if (fieldValues[key] === null) {
          fieldValues[key] = undefined;
        }
      });
      // convert dates to yyyy-MM-dd
      ['disputeReminder', 'taxReminder', 'caseReminder', 'jantriDate'].forEach(
        key => {
          if (editData[key]) {
            fieldValues[key] = editData[key].split('T')[0];
          }
        },
      );
      reset(fieldValues);

      // Load uploaded files
      const uploadFields: UploadField[] = [
        'jantriDocument',
        'houseTaxBill',
        'caseDocument',
        'photos',
        'commercialPropertiesList',
        'residentialPropertiesList',
        'agriculturalLandsOnRent',
        'tenantDisputeList',
        'litigationCopies',
        'rentReceipts',
        'waterBills',
        'propertyTaxBills',
        'gasConnectionBills',
        'digitalRecordsOfBills',
        'listOfCases',
        'caseFiles',
        'interimFinalOrders',
        'judgmentCopies',
        'appealsFiled',
        'closedCases',
        'ongoingCases',
        'advocateDetails',
      ];

      const loadedUploads: Record<UploadField, UploadedFile[]> = {
        jantriDocument: [],
        houseTaxBill: [],
        caseDocument: [],
        photos: [],
        commercialPropertiesList: [],
        residentialPropertiesList: [],
        agriculturalLandsOnRent: [],
        tenantDisputeList: [],
        litigationCopies: [],
        rentReceipts: [],
        waterBills: [],
        propertyTaxBills: [],
        gasConnectionBills: [],
        digitalRecordsOfBills: [],
        listOfCases: [],
        caseFiles: [],
        interimFinalOrders: [],
        judgmentCopies: [],
        appealsFiled: [],
        closedCases: [],
        ongoingCases: [],
        advocateDetails: [],
      };

      uploadFields.forEach(field => {
        if (editData[field]) {
          const urls = editData[field].split(',');
          loadedUploads[field] = urls.map(url => ({
            url,
            uploadedAt: Date.now(),
          }));
        }
      });

      setUploads(loadedUploads);
    }
    if (!isOpen) {
      // reset local state when closed
      reset();
      setUploads({
        jantriDocument: [],
        houseTaxBill: [],
        caseDocument: [],
        photos: [],
        commercialPropertiesList: [],
        residentialPropertiesList: [],
        agriculturalLandsOnRent: [],
        tenantDisputeList: [],
        litigationCopies: [],
        rentReceipts: [],
        waterBills: [],
        propertyTaxBills: [],
        gasConnectionBills: [],
        digitalRecordsOfBills: [],
        listOfCases: [],
        caseFiles: [],
        interimFinalOrders: [],
        judgmentCopies: [],
        appealsFiled: [],
        closedCases: [],
        ongoingCases: [],
        advocateDetails: [],
      });
    }
  }, [isOpen, editData, reset, setValue]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop="static" centered size="lg">
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        Add Sub Property
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className="overflow-hidden">
          {/* =================  BASIC INFORMATION ================= */}
          <h5 className="mb-3">Basic Information</h5>
          <Row>
            <Col lg={12}>
              <FormGroup>
                <Label for="name">Name *</Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="name"
                      type="text"
                      placeholder="Enter sub property name"
                      invalid={!!errors.name}
                    />
                  )}
                />
                {errors.name && (
                  <span className="text-danger">{errors.name.message}</span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="size">Size (Sq. ft)</Label>
                <Controller
                  name="size"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="size"
                      type="number"
                    />
                  )}
                />
                {errors.size && (
                  <span className="text-danger">{errors.size.message}</span>
                )}
              </FormGroup>
            </Col>

            <Col lg={6}>
              <FormGroup>
                <Label for="assesseeNumber">Assessee Number</Label>
                <Controller
                  name="assesseeNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalTextInputProps(field)}
                      id="assesseeNumber"
                      type="text"
                      invalid={!!errors.assesseeNumber}
                    />
                  )}
                />
                {errors.assesseeNumber && (
                  <span className="text-danger">
                    {errors.assesseeNumber.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="shopNumber">House / Shop Number *</Label>
                <Controller
                  name="shopNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="shopNumber"
                      type="text"
                      invalid={!!errors.shopNumber}
                    />
                  )}
                />
                {errors.shopNumber && (
                  <span className="text-danger">
                    {errors.shopNumber.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FloorSelect
                control={control}
                floor={props?.floor}
                cellar={props?.cellar}
                onOtherSelect={setIsCustomFloor}
              />
              {errors.floor && (
                <span className="text-danger">{errors.floor.message}</span>
              )}
            </Col>
          </Row>

          <Row>
            <Col lg={6} />
            <Col lg={6}>
              {isCustomFloor && (
                <Controller
                  name="customFloor"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalTextInputProps(field)}
                      type="text"
                      placeholder="Enter floor details"
                      list="custom-floor-list"
                    />
                  )}
                />
              )}
            </Col>
          </Row>

          <hr className="my-4" />

          {/* =================  FINANCIAL DETAILS ================= */}
          <h5 className="mb-3">Financial Details</h5>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="rentAmount">Rent Amount</Label>
                <Controller
                  name="rentAmount"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="rentAmount"
                      type="number"
                    />
                  )}
                />
                {errors.rentAmount && (
                  <span className="text-danger">
                    {errors.rentAmount.message}
                  </span>
                )}
              </FormGroup>
            </Col>

            <Col lg={6}>
              <FormGroup>
                <Label for="purchaseConsideration">
                  Purchase Consideration
                </Label>
                <Controller
                  name="purchaseConsideration"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="purchaseConsideration"
                      type="number"
                      invalid={!!errors.purchaseConsideration}
                    />
                  )}
                />
                {errors.purchaseConsideration && (
                  <span className="text-danger">
                    {errors.purchaseConsideration.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="stampDuty">Stamp Duty</Label>
                <Controller
                  name="stampDuty"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="stampDuty"
                      type="number"
                      invalid={!!errors.stampDuty}
                    />
                  )}
                />
                {errors.stampDuty && (
                  <span className="text-danger">
                    {errors.stampDuty.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="lastRenovation">Last Renovation Date</Label>
                <Controller
                  name="lastRenovation"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalDateInputProps(field)}
                      id="lastRenovation"
                      type="date"
                      invalid={!!errors.lastRenovation}
                    />
                  )}
                />
                {errors.lastRenovation && (
                  <span className="text-danger">
                    {errors.lastRenovation.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* =================  RENT INCREMENT ================= */}
          <h5 className="mb-3">Rent Increment</h5>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="rentIncrementPercentage">Rent Increment (%)</Label>
                <Controller
                  name="rentIncrementPercentage"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="rentIncrementPercentage"
                      type="number"
                      invalid={!!errors.rentIncrementPercentage}
                    />
                  )}
                />
                {errors.rentIncrementPercentage && (
                  <span className="text-danger">
                    {errors.rentIncrementPercentage.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="rentIncrementDuration">
                  Rent Increment Duration (years)
                </Label>
                <Controller
                  name="rentIncrementDuration"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="rentIncrementDuration"
                      type="number"
                      invalid={!!errors.rentIncrementDuration}
                    />
                  )}
                />
                {errors.rentIncrementDuration && (
                  <span className="text-danger">
                    {errors.rentIncrementDuration.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* =================  REMINDERS ================= */}
          <h5 className="mb-3">Reminders</h5>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="taxReminder">Tax Reminder Date</Label>
                <Controller
                  name="taxReminder"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalDateInputProps(field)}
                      id="taxReminder"
                      type="date"
                      invalid={!!errors.taxReminder}
                    />
                  )}
                />
                {errors.taxReminder && (
                  <span className="text-danger">
                    {errors.taxReminder.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="taxReminderMargin">
                  Tax Reminder Margin (days)
                </Label>
                <Controller
                  name="taxReminderMargin"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="taxReminderMargin"
                      type="number"
                      invalid={!!errors.taxReminderMargin}
                    />
                  )}
                />
                {errors.taxReminderMargin && (
                  <span className="text-danger">
                    {errors.taxReminderMargin.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="disputeReminder">Dispute Reminder Date</Label>
                <Controller
                  name="disputeReminder"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="disputeReminder"
                      type="date"
                      invalid={!!errors.disputeReminder}
                    />
                  )}
                />
                {errors.disputeReminder && (
                  <span className="text-danger">
                    {errors.disputeReminder.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="disputeReminderMargin">
                  Dispute Reminder Margin (days)
                </Label>
                <Controller
                  name="disputeReminderMargin"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="disputeReminderMargin"
                      type="number"
                      invalid={!!errors.disputeReminderMargin}
                    />
                  )}
                />
                {errors.disputeReminderMargin && (
                  <span className="text-danger">
                    {errors.disputeReminderMargin.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="caseReminder">Case Reminder Date</Label>
                <Controller
                  name="caseReminder"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalDateInputProps(field)}
                      id="caseReminder"
                      type="date"
                      invalid={!!errors.caseReminder}
                    />
                  )}
                />
                {errors.caseReminder && (
                  <span className="text-danger">
                    {errors.caseReminder.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="caseNumber">Case Number</Label>
                <Controller
                  name="caseNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalTextInputProps(field)}
                      id="caseNumber"
                      type="text"
                      invalid={!!errors.caseNumber}
                    />
                  )}
                />
                {errors.caseNumber && (
                  <span className="text-danger">
                    {errors.caseNumber.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* =================  JANTRI ================= */}
          <h5 className="mb-3">Jantri Details</h5>
          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="jantri">Jantri</Label>
                <Controller
                  name="jantri"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalNumberInputProps(field)}
                      id="jantri"
                      type="number"
                      invalid={!!errors.jantri}
                    />
                  )}
                />
                {errors.jantri && (
                  <span className="text-danger">{errors.jantri.message}</span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="jantriDate">Jantri Date</Label>
                <Controller
                  name="jantriDate"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...optionalDateInputProps(field)}
                      id="jantriDate"
                      type="date"
                      invalid={!!errors.jantriDate}
                    />
                  )}
                />
                {errors.jantriDate && (
                  <span className="text-danger">
                    {errors.jantriDate.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* =================  DOCUMENTS ================= */}
          <h5 className="mb-3">Documents</h5>

          <Row>
            <Col lg={4}>
              <FormGroup>
                <Label for="jantriDocument">Jantri Document</Label>
                <Controller
                  name="jantriDocument"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="jantriDocument"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'jantriDocument')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.jantriDocument}
                  onRemove={i => removeUploadedFile('jantriDocument', i)}
                />
                {errors.jantriDocument && (
                  <span className="text-danger">
                    {errors.jantriDocument.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={4}>
              <FormGroup>
                <Label for="houseTaxBill">House Tax Bill</Label>
                <Controller
                  name="houseTaxBill"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="houseTaxBill"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'houseTaxBill')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.houseTaxBill}
                  onRemove={i => removeUploadedFile('houseTaxBill', i)}
                />
                {errors.houseTaxBill && (
                  <span className="text-danger">
                    {errors.houseTaxBill.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={4}>
              <FormGroup>
                <Label for="caseDocument">Case Document</Label>
                <Input
                  id="caseDocument"
                  multiple
                  type="file"
                  onChange={e =>
                    handleMultiUpload(e.target.files, 'caseDocument')
                  }
                />
                <FilePreview
                  files={uploads.caseDocument}
                  onRemove={i => removeUploadedFile('caseDocument', i)}
                />
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />
          <h5 className="mb-3">Property Lists & Records</h5>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="commercialPropertiesList">
                  List of Commercial Properties
                </Label>
                <Controller
                  name="commercialPropertiesList"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="commercialPropertiesList"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(
                          e.target.files,
                          'commercialPropertiesList',
                        )
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.commercialPropertiesList}
                  onRemove={i =>
                    removeUploadedFile('commercialPropertiesList', i)
                  }
                />
                {errors.commercialPropertiesList && (
                  <span className="text-danger">
                    {errors.commercialPropertiesList.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="residentialPropertiesList">
                  List of Residential Properties
                </Label>
                <Controller
                  name="residentialPropertiesList"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="residentialPropertiesList"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(
                          e.target.files,
                          'residentialPropertiesList',
                        )
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.residentialPropertiesList}
                  onRemove={i =>
                    removeUploadedFile('residentialPropertiesList', i)
                  }
                />
                {errors.residentialPropertiesList && (
                  <span className="text-danger">
                    {errors.residentialPropertiesList.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="agriculturalLandsOnRent">
                  List of Agricultural Lands on Rent
                </Label>
                <Controller
                  name="agriculturalLandsOnRent"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="agriculturalLandsOnRent"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(
                          e.target.files,
                          'agriculturalLandsOnRent',
                        )
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.agriculturalLandsOnRent}
                  onRemove={i =>
                    removeUploadedFile('agriculturalLandsOnRent', i)
                  }
                />
                {errors.agriculturalLandsOnRent && (
                  <span className="text-danger">
                    {errors.agriculturalLandsOnRent.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="tenantDisputeList">Tenant Dispute List</Label>
                <Controller
                  name="tenantDisputeList"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="tenantDisputeList"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'tenantDisputeList')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.tenantDisputeList}
                  onRemove={i => removeUploadedFile('tenantDisputeList', i)}
                />
                {errors.tenantDisputeList && (
                  <span className="text-danger">
                    {errors.tenantDisputeList.message}
                  </span>
                )}
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />
          <h5 className="mb-3">Bills & Receipts</h5>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="rentReceipts">Rent Receipts</Label>
                <Input
                  id="rentReceipts"
                  multiple
                  type="file"
                  onChange={e =>
                    handleMultiUpload(e.target.files, 'rentReceipts')
                  }
                />
                <FilePreview
                  files={uploads.rentReceipts}
                  onRemove={i => removeUploadedFile('rentReceipts', i)}
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="waterBills">Water Bills/Receipts</Label>
                <Controller
                  name="waterBills"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="waterBills"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'waterBills')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.waterBills}
                  onRemove={i => removeUploadedFile('waterBills', i)}
                />
                {errors.waterBills && (
                  <span className="text-danger">
                    {errors.waterBills.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="propertyTaxBills">
                  Property Tax Bills/Receipts
                </Label>
                <Controller
                  name="propertyTaxBills"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="propertyTaxBills"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'propertyTaxBills')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.propertyTaxBills}
                  onRemove={i => removeUploadedFile('propertyTaxBills', i)}
                />
                {errors.propertyTaxBills && (
                  <span className="text-danger">
                    {errors.propertyTaxBills.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="gasConnectionBills">Gas Connection Bills</Label>
                <Input
                  id="gasConnectionBills"
                  multiple
                  type="file"
                  onChange={e =>
                    handleMultiUpload(e.target.files, 'gasConnectionBills')
                  }
                />
                <FilePreview
                  files={uploads.gasConnectionBills}
                  onRemove={i => removeUploadedFile('gasConnectionBills', i)}
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="digitalRecordsOfBills">
                  Digital Records of Bills
                </Label>
                <Input
                  id="digitalRecordsOfBills"
                  multiple
                  type="file"
                  onChange={e =>
                    handleMultiUpload(e.target.files, 'digitalRecordsOfBills')
                  }
                />
                <FilePreview
                  files={uploads.digitalRecordsOfBills}
                  onRemove={i => removeUploadedFile('digitalRecordsOfBills', i)}
                />
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />
          <h5 className="mb-3">Legal Documents & Cases</h5>

          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="litigationCopies">Litigation Copies</Label>
                <Controller
                  name="litigationCopies"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="litigationCopies"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'litigationCopies')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.litigationCopies}
                  onRemove={i => removeUploadedFile('litigationCopies', i)}
                />
                {errors.litigationCopies && (
                  <span className="text-danger">
                    {errors.litigationCopies.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="listOfCases">List of Cases</Label>
                <Controller
                  name="listOfCases"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="listOfCases"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'listOfCases')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.listOfCases}
                  onRemove={i => removeUploadedFile('listOfCases', i)}
                />
                {errors.listOfCases && (
                  <span className="text-danger">
                    {errors.listOfCases.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="caseFiles">Case Files</Label>
                <Controller
                  name="caseFiles"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="caseFiles"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'caseFiles')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.caseFiles}
                  onRemove={i => removeUploadedFile('caseFiles', i)}
                />
                {errors.caseFiles && (
                  <span className="text-danger">
                    {errors.caseFiles.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="interimFinalOrders">Interim/Final Orders</Label>
                <Controller
                  name="interimFinalOrders"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="interimFinalOrders"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'interimFinalOrders')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.interimFinalOrders}
                  onRemove={i => removeUploadedFile('interimFinalOrders', i)}
                />
                {errors.interimFinalOrders && (
                  <span className="text-danger">
                    {errors.interimFinalOrders.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="judgmentCopies">Judgment Copies</Label>
                <Controller
                  name="judgmentCopies"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="judgmentCopies"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'judgmentCopies')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.judgmentCopies}
                  onRemove={i => removeUploadedFile('judgmentCopies', i)}
                />
                {errors.judgmentCopies && (
                  <span className="text-danger">
                    {errors.judgmentCopies.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="appealsFiled">Appeals Filed</Label>
                <Controller
                  name="appealsFiled"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="appealsFiled"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'appealsFiled')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.appealsFiled}
                  onRemove={i => removeUploadedFile('appealsFiled', i)}
                />
                {errors.appealsFiled && (
                  <span className="text-danger">
                    {errors.appealsFiled.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="closedCases">Closed Cases</Label>
                <Controller
                  name="closedCases"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="closedCases"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'closedCases')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.closedCases}
                  onRemove={i => removeUploadedFile('closedCases', i)}
                />
                {errors.closedCases && (
                  <span className="text-danger">
                    {errors.closedCases.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="ongoingCases">Ongoing Cases</Label>
                <Controller
                  name="ongoingCases"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="ongoingCases"
                      multiple
                      type="file"
                      onChange={e =>
                        handleMultiUpload(e.target.files, 'ongoingCases')
                      }
                    />
                  )}
                />
                <FilePreview
                  files={uploads.ongoingCases}
                  onRemove={i => removeUploadedFile('ongoingCases', i)}
                />
                {errors.ongoingCases && (
                  <span className="text-danger">
                    {errors.ongoingCases.message}
                  </span>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="advocateDetails">Advocate Details</Label>
                <Input
                  id="advocateDetails"
                  multiple
                  type="file"
                  onChange={e =>
                    handleMultiUpload(e.target.files, 'advocateDetails')
                  }
                />
                <FilePreview
                  files={uploads.advocateDetails}
                  onRemove={i => removeUploadedFile('advocateDetails', i)}
                />
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* =================  PHOTOS ================= */}
          <h5 className="mb-3">Photos</h5>

          <FormGroup>
            <Label for="photos">Photos</Label>
            <Controller
              name="photos"
              control={control}
              render={({ field }) => (
                <Input
                  id="photos"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={e => handleMultiUpload(e.target.files, 'photos')}
                />
              )}
            />
            <FilePreview
              files={uploads.photos}
              onRemove={i => removeUploadedFile('photos', i)}
            />
            {errors.photos && (
              <span className="text-danger">{errors.photos.message}</span>
            )}
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={toggle} type="button">
            Cancel
          </Button>
          <Button color="primary" type="submit">
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default SubPropertyModal;
